import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { ProductCard, type Product } from '@/components/product/ProductCard';

interface SellerProfile {
  handle: string;
  name: string;
  avatarUrl?: string;
  rating: number;
  ratingCount: number;
  tier: 'GOLD' | 'TRUSTED' | 'STANDARD' | 'WATCH';
  joinedYear: number;
  soldCount: number;
  listings: Product[];
  // Follow (Souqli Membership perk)
  followable: boolean;
  followerCount: number;
  following: boolean;
  canFollow: boolean;
  isSelf: boolean;
}
interface SellerCard {
  handle: string;
  name: string;
  avatarUrl?: string;
  rating: number;
  tier: SellerProfile['tier'];
  soldCount: number;
}

const tierLabel: Record<SellerProfile['tier'], string> = {
  GOLD: 'Gold Seller', TRUSTED: 'Trusted Seller', STANDARD: 'Seller', WATCH: 'New Seller',
};
const tierPill: Record<SellerProfile['tier'], string> = {
  GOLD: 'bg-gold text-ink', TRUSTED: 'bg-sage text-cream', STANDARD: 'bg-clay/80 text-cream', WATCH: 'bg-cream/10 text-cream/80',
};

/** A seller's real public storefront - their live listings, rating, and sales. */
export default function SellerWardrobe() {
  const { handle = '' } = useParams();

  const { data: seller, isLoading, isError } = useQuery<SellerProfile>({
    queryKey: ['seller', handle],
    queryFn: async () => (await api.get(`/sellers/${handle}`)).data,
    retry: false,
  });
  const { data: others = [] } = useQuery<SellerCard[]>({
    queryKey: ['sellers'],
    queryFn: async () => (await api.get('/sellers')).data,
  });

  if (isLoading) {
    return <div className="bg-cream min-h-[70vh] p-10"><div className="skeleton h-48 rounded-luxe max-w-[1800px] mx-auto" /></div>;
  }
  if (isError || !seller) {
    return (
      <div className="bg-cream min-h-[70vh] grid place-items-center text-center px-6">
        <div>
          <div className="display text-3xl text-mute">Seller not found</div>
          <p className="text-sm text-mute mt-2">@{handle} doesn't have a Souqli wardrobe.</p>
          <Link to="/sellers" className="btn btn-outline mt-5">Browse sellers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-[70vh]">
      {/* Header */}
      <section className="section-dark relative bg-ink text-cream overflow-hidden">
        <div className="absolute inset-0 mashrabiya mashrabiya-soft pointer-events-none" />
        <div className="relative max-w-[1800px] mx-auto px-6 md:px-10 py-12">
          <nav className="stamp text-cream/50 mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-gold">Home</Link><span>/</span>
            <Link to="/sellers" className="hover:text-gold">Sellers</Link><span>/</span>
            <span className="text-gold">@{seller.handle}</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-end gap-5">
            {seller.avatarUrl ? (
              <img src={seller.avatarUrl} alt={seller.name} className="w-24 h-24 rounded-full object-cover border-4 border-gold/40 shadow-glow shrink-0" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gold/15 border-4 border-gold/40 shadow-glow shrink-0 grid place-items-center text-3xl font-medium text-gold">
                {seller.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className={`pill text-[10px] ${tierPill[seller.tier]}`}>{tierLabel[seller.tier]}</span>
                <span className="pill bg-cream/10 text-cream/80 text-[10px]">Selling since {seller.joinedYear}</span>
              </div>
              <h1 className="display text-4xl leading-none">{seller.name}</h1>
              <div className="stamp text-cream/50 mt-1.5">@{seller.handle}</div>
              <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4">
                <Stat n={String(seller.listings.length)} label="live listings" />
                <Stat n={String(seller.soldCount)} label="sold" />
                <Stat n={`★ ${seller.rating ? seller.rating.toFixed(2) : '-'}`} label={`${seller.ratingCount} rating${seller.ratingCount === 1 ? '' : 's'}`} />
                {seller.followable && !seller.isSelf && (
                  <Stat n={String(seller.followerCount)} label={`follower${seller.followerCount === 1 ? '' : 's'}`} />
                )}
              </div>
            </div>
            <FollowControl seller={seller} handle={handle} />
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="max-w-[1800px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="stamp text-gold-dark text-[9px]">IN THIS WARDROBE</div>
            <h2 className="display text-2xl md:text-3xl mt-1.5">{seller.name.split(' ')[0]}'s current listings</h2>
          </div>
          <Link to="/c/all" className="btn btn-outline btn-sm shrink-0">Browse all →</Link>
        </div>
        {seller.listings.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8">
            {seller.listings.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="card p-10 text-center text-mute text-sm">Nothing live right now - check back soon.</div>
        )}
      </section>

      {/* Other sellers */}
      {others.filter((x) => x.handle !== seller.handle).length > 0 && (
        <section className="bg-white border-t border-warmline py-12">
          <div className="max-w-[1800px] mx-auto px-6 md:px-10">
            <div className="stamp text-gold-dark text-[9px] mb-1">MORE WARDROBES</div>
            <h2 className="display text-2xl mb-6">Other sellers worth following</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {others.filter((x) => x.handle !== seller.handle).slice(0, 5).map((x) => (
                <Link key={x.handle} to={`/seller/${x.handle}`} className="card-flat p-4 text-center hover:shadow-luxe hover:-translate-y-0.5 transition group">
                  {x.avatarUrl ? (
                    <img src={x.avatarUrl} alt={x.name} className="w-14 h-14 rounded-full object-cover mx-auto border-2 border-gold/30" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gold/15 border-2 border-gold/30 mx-auto grid place-items-center text-lg font-medium text-gold-dark">
                      {x.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="text-sm font-medium mt-2 truncate group-hover:text-gold-dark transition">{x.name}</div>
                  <div className="stamp text-mute text-[9px] truncate">@{x.handle}</div>
                  <div className="text-[11px] text-mute mt-1">{x.soldCount} sold · ★ {x.rating ? x.rating.toFixed(1) : '-'}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <div className="display text-xl text-gold leading-none">{n}</div>
      <div className="stamp text-cream/45 text-[9px] mt-1">{label}</div>
    </div>
  );
}

/**
 * Follow control - a Souqli Membership perk.
 *
 * Only shown for followable (member) sellers, and never on your own storefront.
 * A member buyer toggles follow; a signed-out visitor is sent to sign in, and a
 * signed-in non-member is pointed at Membership (the server enforces both).
 */
function FollowControl({ seller, handle }: { seller: SellerProfile; handle: string }) {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const mutate = useMutation({
    mutationFn: async (next: boolean) =>
      (await (next ? api.post(`/sellers/${handle}/follow`) : api.delete(`/sellers/${handle}/follow`))).data as { following: boolean; followerCount: number },
    onSuccess: (data) => {
      qc.setQueryData(['seller', handle], (old: SellerProfile | undefined) =>
        old ? { ...old, following: data.following, followerCount: data.followerCount } : old);
      toast.success(data.following
        ? `Following ${seller.name.split(' ')[0]} - we'll alert you to new drops.`
        : `Unfollowed ${seller.name.split(' ')[0]}.`);
    },
    onError: (e: any) => {
      if (e?.response?.data?.code === 'MEMBERSHIP_REQUIRED') {
        toast('Following sellers is a Souqli Membership perk.', { icon: '✦' });
        navigate('/membership');
      } else {
        toast.error(e?.response?.data?.message ?? 'Could not update follow.');
      }
    },
  });

  if (seller.isSelf || !seller.followable) return null;

  const onClick = () => {
    if (!user) { navigate('/login'); return; }
    if (!seller.canFollow) {
      toast('Following sellers is a Souqli Membership perk.', { icon: '✦' });
      navigate('/membership');
      return;
    }
    mutate.mutate(!seller.following);
  };

  return (
    <div className="sm:self-center shrink-0">
      <button
        onClick={onClick}
        disabled={mutate.isPending}
        aria-pressed={seller.following}
        className={`btn btn-sm inline-flex items-center gap-1.5 disabled:opacity-60 ${
          seller.following ? 'btn-outline-cream' : 'btn-gold !text-ink'
        }`}
      >
        {seller.following ? '✓ Following' : '＋ Follow'}
      </button>
    </div>
  );
}
